import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { Loader2, RotateCcw, Trash2 } from "lucide-react";

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function TransactionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [sale, setSale] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [refunding, setRefunding] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = async () => {
    const r = await api.get(`/admin/sales/${id}`);
    setSale(r.data);
    setSelected(new Set());
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const toggle = (inv_id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(inv_id) ? next.delete(inv_id) : next.add(inv_id);
      return next;
    });
  };

  const doRefund = async () => {
    setError(""); setSuccess(""); setRefunding(true);
    try {
      const r = await api.post(`/admin/sales/${id}/refund`, { inventory_ids: Array.from(selected) });
      const parts = [`Refunded ${r.data.refunded_items} item(s) · $${r.data.refunded_subtotal.toFixed(2)}`];
      if (r.data.points_reversed) parts.push(`-${r.data.points_reversed} pts reversed`);
      if (r.data.points_redeemed_restored) parts.push(`+${r.data.points_redeemed_restored} pts restored`);
      setSuccess(parts.join(" · "));
      setConfirm(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Refund failed");
      setConfirm(false);
    } finally {
      setRefunding(false);
    }
  };

  const doDelete = async () => {
    setError(""); setSuccess(""); setDeleting(true);
    try {
      await api.delete(`/admin/sales/${id}`);
      nav("/pos/transactions");
    } catch (e) {
      setError(e.response?.data?.detail || "Delete failed");
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  if (!sale) return <Layout><div className="max-w-4xl mx-auto px-6 py-16"><Loader2 className="animate-spin" /></div></Layout>;

  const refundableItems = sale.items.filter((it) => !it.refunded);
  const selectedSubtotal = sale.items.filter((it) => selected.has(it.inventory_id)).reduce((s, it) => s + Number(it.price), 0);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => nav("/pos/transactions")} className="label-eyebrow inline-block" data-testid="td-back">← Back to Transactions</button>
          <button onClick={() => setConfirmDelete(true)} className="text-xs label-eyebrow inline-flex items-center gap-1 hover:text-[var(--ab-danger)]" style={{ color: "var(--ab-text-dim)" }} data-testid="td-delete-btn">
            <Trash2 size={12} /> Delete Transaction
          </button>
        </div>

        <div className="label-eyebrow mb-2" style={{ color: "var(--ab-primary)" }}>Sale</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black mb-1" data-testid="td-customer">{sale.member_name}</h1>
        {sale.member_phone && <div className="font-mono text-sm" style={{ color: "var(--ab-text-muted)" }}>Account #{sale.member_phone}</div>}
        <div className="text-sm mt-1" style={{ color: "var(--ab-text-muted)" }}>{fmtDate(sale.completed_at)}</div>

        <div className="grid sm:grid-cols-4 gap-3 my-8">
          <Stat label="Subtotal" value={`$${Number(sale.subtotal).toFixed(2)}`} />
          <Stat label="Discount" value={`-$${Number(sale.discount_amount || 0).toFixed(2)}`} />
          <Stat label="Total" value={`$${Number(sale.total).toFixed(2)}`} highlight />
          <Stat label="Refunded" value={`$${Number(sale.refunded_subtotal || 0).toFixed(2)}`} />
        </div>

        {sale.fully_refunded && (
          <div className="mb-4 px-4 py-3 text-sm" style={{ border: "1px solid var(--ab-danger)", color: "var(--ab-danger)" }} data-testid="td-fully-refunded">
            This sale has been fully refunded.
          </div>
        )}

        <h3 className="label-eyebrow mb-3">Items</h3>
        <div className="surface divide-y mb-6" style={{ borderColor: "var(--ab-border)" }}>
          {sale.items.map((it) => (
            <label key={it.inventory_id} className="flex items-center px-5 py-3 gap-3 cursor-pointer" style={{ opacity: it.refunded ? 0.4 : 1 }} data-testid={`td-item-${it.inventory_id}`}>
              <input
                type="checkbox"
                disabled={it.refunded}
                checked={selected.has(it.inventory_id)}
                onChange={() => toggle(it.inventory_id)}
                data-testid={`td-check-${it.inventory_id}`}
              />
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{it.title}</div>
                <div className="text-xs" style={{ color: "var(--ab-text-dim)" }}>{it.console}</div>
              </div>
              <div className="font-mono font-bold text-right" style={{ color: "var(--ab-primary)" }}>${Number(it.price).toFixed(2)}</div>
              {it.refunded && <span className="text-[10px] uppercase tracking-widest" style={{ color: "var(--ab-danger)" }}>Refunded</span>}
            </label>
          ))}
        </div>

        {error && <div className="mb-3 text-sm" style={{ color: "var(--ab-danger)" }} data-testid="td-error">{error}</div>}
        {success && <div className="mb-3 text-sm" style={{ color: "var(--ab-primary)" }} data-testid="td-success">{success}</div>}

        {refundableItems.length > 0 && (
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm" style={{ color: "var(--ab-text-muted)" }}>
              {selected.size > 0 ? <>Refund <b style={{ color: "var(--ab-text)" }}>{selected.size}</b> item{selected.size === 1 ? "" : "s"} · <b style={{ color: "var(--ab-primary)" }}>${selectedSubtotal.toFixed(2)}</b></> : "Select items to refund."}
            </div>
            <button
              disabled={selected.size === 0 || refunding}
              onClick={() => setConfirm(true)}
              className="btn-danger inline-flex items-center gap-2"
              data-testid="td-refund-btn"
            >
              <RotateCcw size={14} /> Refund Selected
            </button>
          </div>
        )}

        {confirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-6" style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }} onClick={() => !refunding && setConfirm(false)}>
            <div className="surface p-7 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="td-confirm-modal">
              <h3 className="font-display text-2xl font-bold mb-2">Confirm Refund</h3>
              <p className="text-sm mb-6" style={{ color: "var(--ab-text-muted)" }}>
                Refund {selected.size} item(s) totaling ${selectedSubtotal.toFixed(2)}? Items will be returned to inventory.
                {sale.member_phone && <> The member's points & lifetime spend will be adjusted automatically.</>}
              </p>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setConfirm(false)} disabled={refunding} className="btn-secondary" data-testid="td-confirm-no">Cancel</button>
                <button onClick={doRefund} disabled={refunding} className="btn-danger inline-flex items-center gap-2" data-testid="td-confirm-yes">
                  {refunding ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        {confirmDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-6" style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }} onClick={() => !deleting && setConfirmDelete(false)}>
            <div className="surface p-7 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="td-delete-modal">
              <h3 className="font-display text-2xl font-bold mb-2">Delete Transaction?</h3>
              <p className="text-sm mb-6" style={{ color: "var(--ab-text-muted)" }}>
                This permanently removes the sale record. Inventory and member balances are <b>not</b> adjusted — refund first if you need those side effects. Use this to clear test transactions.
              </p>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setConfirmDelete(false)} disabled={deleting} className="btn-secondary" data-testid="td-delete-no">Cancel</button>
                <button onClick={doDelete} disabled={deleting} className="btn-danger inline-flex items-center gap-2" data-testid="td-delete-yes">
                  {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className="surface p-4">
      <div className="label-eyebrow mb-2">{label}</div>
      <div className="font-mono font-bold text-2xl" style={{ color: highlight ? "var(--ab-primary)" : "var(--ab-text)" }}>{value}</div>
    </div>
  );
}
